import axios from "axios";
import { parseJobDescriptionText } from "@/utils/job_parser_utils";
import {
  CREATE_ENTITY_TYPE_URL,
  GET_JOB_PROFILE_DETAILS_URL,
} from "@/utils/constants/constants.js";
import { validate_non_empty_string } from "@/utils/validations/validate_inputs";

function build_default_profile_name(name) {
  return name?.trim() ? name : `Untitled Job Profile (${Date.now()})`;
}

function build_default_profile_description(description) {
  return description?.trim() ? description : "Pending AI extraction";
}
function normalize_project_id(project_id) {
  return Number.isInteger(Number(project_id)) ? Number(project_id) : 0;
}

function build_entity_type_sub_entities(context) {
  const filtered_new = context.new_subentities.filter((name) => name.trim() !== "");
  const filtered_existing = context.sub_entities
    .filter((entity) => context.selected_subentities.includes(entity.id))
    .map((entity) => entity.sub_entity_name);
  return [...filtered_new, ...filtered_existing];
}

function get_extraction_payload(details_response) {
  return details_response?.data || details_response;
}

function get_extracted_document(details_response) {
  return details_response?.extracted_document;
}

function get_profile_title_candidates(payload) {
  return [payload.job_title, payload.title];
}

function get_first_non_empty_title(payload) {
  return get_profile_title_candidates(payload).find((value) => validate_non_empty_string(value)) || "";
}

function has_structured_sections(payload) {
  return Array.isArray(payload?.sections) && payload.sections.length > 0;
}

function build_resolved_extracted_title(context, extracted_title) {
  if (context.is_generic_extraction_title(extracted_title)) {
    return context.trust_type_name || "";
  }
  return get_first_non_empty_title({ job_title: extracted_title, title: context.trust_type_name });
}

function resolve_extracted_title(context, payload) {
  const extracted_title = get_first_non_empty_title(payload);
  return build_resolved_extracted_title(context, extracted_title);
}

function should_sync_profile_title(context, extracted_title) {
  return !validate_non_empty_string(context.trust_type_name)
    && !!extracted_title
    && !context.is_generic_extraction_title(extracted_title);
}

function build_extraction_steps(file) {
  return [
    { label: "Backend response received - raw_payload.text present", status: "wait" },
    { label: `${Math.floor(file.size / 1024)} KB document loaded`, status: "wait" },
    { label: "Parsing sections A through J from flat text...", status: "wait" },
    { label: "Cleaning broken column words & page markers", status: "wait" },
    { label: "Structuring responsibilities, outputs & competencies", status: "wait" },
    { label: "Populating 10 editor section cards", status: "wait" },
  ];
}

function get_extraction_pending_message(details_response) {
  return details_response?.status === "pending" || details_response?.extraction_status === "pending_ai_extraction"
    ? "AI extraction is still processing. Please wait a bit and try again."
    : "AI extraction did not return structured sections. Please try again or continue manually.";
}

function sync_profile_description(context, payload, normalized_sections) {
  context.create_job_description = context.dedupe_global_description(payload.description || context.trust_type_description || "", normalized_sections);
  sync_missing_trust_type_description(context, payload.description);
}
function sync_missing_trust_type_description(context, description) {
  const should_sync_description = !validate_non_empty_string(context.trust_type_description) && validate_non_empty_string(description);
  if (should_sync_description) context.trust_type_description = description;
}
function apply_sections_payload(context, payload) {
  const normalized_sections = context.normalize_sections(Array.isArray(payload?.sections) ? payload.sections : []);
  context.create_job_title = resolve_extracted_title(context, payload || {});
  sync_profile_description(context, payload || {}, normalized_sections);
  return normalized_sections;
}
function sync_profile_title(context, payload) {
  const extracted_title = get_first_non_empty_title(payload || {});
  if (should_sync_profile_title(context, extracted_title)) context.trust_type_name = extracted_title;
}
function apply_structured_payload(context, payload) {
  if (!has_structured_sections(payload)) return false;
  context.apply_create_profile_payload(payload);
  return context.create_job_sections.length > 0;
}
function get_first_section_text(context, sections) {
  const first_section = context.safe_array_get(sections, 0);
  return Array.isArray(first_section?.descriptions) ? context.safe_array_get(first_section.descriptions, 0) : null;
}
function extract_parsed_job(details_response, fallbackParser) {
  return details_response?.parsed || fallbackParser(details_response);
}
function should_parse_raw_job_text(rawText) {
  return typeof rawText === "string" && rawText.length > 100;
}

export default {
  methods: {
    build_entity_type_payload() {
      const payload = {
        entity_type_name: build_default_profile_name(this.trust_type_name),
        entity_type_description: build_default_profile_description(this.trust_type_description),
        project_id: normalize_project_id(this.project_id),
        entity_type_category: 1,
        sub_entities: build_entity_type_sub_entities(this),
      };
      return payload;
    },
    async ensure_create_entity_type_exists() {
      if (this.create_entity_type_id) return this.create_entity_type_id;
      const response = await axios.post(CREATE_ENTITY_TYPE_URL, this.build_entity_type_payload(), {
        headers: this.headers,
        withCredentials: true,
      });
      if (response.data.status !== "success") throw new Error("Failed to create job profile.");
      this.create_entity_type_id = this.extract_entity_type_id(response.data);
      return this.create_entity_type_id;
    },
    async fetch_job_profile_document(entity_type_id) {
      const response = await axios.get(GET_JOB_PROFILE_DETAILS_URL, { headers: this.headers, params: { entity_type_id } });
      const wrapper = response?.data || {};
      return wrapper.data || wrapper;
    },
    apply_create_profile_payload(payload = {}) {
      const normalized_sections = apply_sections_payload(this, payload);
      sync_profile_title(this, payload);
      this.create_job_sections = normalized_sections;
      this.create_active_section_index = 0;
      this.has_extraction_preview = normalized_sections.length > 0;
    },
    async load_extraction_preview(entity_type_id, { attempts = 20, delay = 1500 } = {}) {
      for (let attempt = 0; attempt < attempts; attempt += 1) {
        const payload = await this.fetch_job_profile_document(entity_type_id);
        if (Array.isArray(payload.sections) && payload.sections.length > 0) {
          this.apply_create_profile_payload(payload);
          return true;
        }
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
      return false;
    },
    handle_extraction_response(details_response) {
      if (details_response?.extraction_warning) this.show_info(`Extraction warning: ${details_response.extraction_warning}`);
      if (details_response?.extraction_skipped_reason) this.show_info(details_response.extraction_skipped_reason);
    },
    async process_extraction_result(details_response, entity_type_id) {
      const payload = get_extraction_payload(details_response);
      if (apply_structured_payload(this, payload)) return true;
      const extracted = get_extracted_document(details_response);
      if (apply_structured_payload(this, extracted)) return true;
      return this.load_extraction_preview(entity_type_id);
    },
    show_extraction_outcome() {
      if (!this.create_job_sections.length) {
        this.show_info("Extraction submitted. No structured sections returned yet.");
        return;
      }
      this.show_success("Extraction completed. Review sections below, then save.");
    },
    parse_raw_text_to_job(details_response) {
      const extracted_document = get_extracted_document(details_response);
      if (!has_structured_sections(extracted_document)) return null;
      const rawText = get_first_section_text(this, extracted_document.sections);
      return should_parse_raw_job_text(rawText) ? parseJobDescriptionText(rawText) : null;
    },
    init_parsed_editor_if_ready(parsed, job_id) {
      if (parsed && job_id) this.init_parsed_editor(parsed, job_id);
    },
    async handle_extraction_workflow(entity_type_id) {
      const details_response = await this.upsert_job_details(entity_type_id, { file: this.job_profile_file }).catch((err) => {
        this.show_error(err?.response?.data?.message || err?.message || "Extraction failed.");
        return null;
      });
      if (!details_response) return null;
      const parsed = extract_parsed_job(details_response, (response) => this.parse_raw_text_to_job(response));
      this.init_parsed_editor_if_ready(parsed, details_response?.job_id);
      this.handle_extraction_response(details_response);
      const ready = await this.process_extraction_result(details_response, entity_type_id);
      const result = { details_response, ready };
      return result;
    },
    async extract_with_ai() {
      if (!this.can_start_ai_extraction()) return;
      this.start_ai_extraction();
      const entity_type_id = await this.resolve_ai_extraction_entity_type();
      if (!entity_type_id) return;
      this.simulate_extraction_progress();
      const extraction_result = await this.handle_extraction_workflow(entity_type_id);
      this.is_extracting_ai = false;
      this.handle_ai_extraction_result(extraction_result);
    },
    can_start_ai_extraction() {
      if (!this.validate_create_trust_type_inputs(true)) return false;
      if (this.job_profile_file instanceof File) return true;
      this.show_error("Please attach a PDF before extraction.");
      return false;
    },
    start_ai_extraction() {
      this.go_to_create_stage("extracting");
      this.extraction_progress_steps = build_extraction_steps(this.job_profile_file);
      this.extraction_current_step = 0;
      this.is_extracting_ai = true;
    },
    async resolve_ai_extraction_entity_type() {
      const entity_type_id = await this.ensure_create_entity_type_exists().catch((err) => {
        this.show_error(this.extract_error_message(err));
        this.go_to_create_stage("create");
        return null;
      });
      if (entity_type_id) return entity_type_id;
      this.is_extracting_ai = false;
      return null;
    },
    handle_ai_extraction_result(extraction_result) {
      if (!extraction_result) return this.reset_ai_extraction_stage();
      const { details_response, ready } = extraction_result;
      if (!ready) {
        this.go_to_create_stage("create");
        this.show_info(get_extraction_pending_message(details_response));
        return;
      }
      this.mark_extraction_progress_complete();
      this.go_to_create_stage("extracted");
      this.show_extraction_outcome();
    },
    reset_ai_extraction_stage() {
      this.go_to_create_stage("create");
    },
  },
};
